import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { History, Scale, Compass, Camera, Library, PenLine, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { useOnThisDay } from '../../../hooks/useOnThisDay'

// "On this day" memory feed on Today — renders only when a past-year
// memory exists for today's calendar date, so most days it costs
// nothing visually.

const KIND_ICON = {
  checkin: PenLine,
  weight: Scale,
  journey: Compass,
  moment: Camera,
  book: Library,
}

export default function OnThisDayCard() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const { items, loading } = useOnThisDay()

  if (loading || !items.length) return null

  return (
    <Card className="border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-transparent">
      <CardContent className="p-3 sm:p-4 space-y-2.5">
        <h2 className="text-sm font-semibold flex items-center gap-1.5" dir="auto">
          <History className="w-4 h-4 text-amber-500" />
          {t('onThisDay.title')}
        </h2>
        <div className="space-y-2">
          {items.slice(0, 4).map((item, i) => {
            const Icon = KIND_ICON[item.kind] || History
            const yearsAgo = new Date().getFullYear() - Number(item.date.slice(0, 4))
            const line = lineFor(item, t)
            if (!line) return null
            const clickable = item.kind === 'moment' && item.meta?.promptId
            const Row = clickable ? 'button' : 'div'
            return (
              <Row
                key={i}
                {...(clickable ? { type: 'button', onClick: () => navigate(`/moments/${item.meta.promptId}`) } : {})}
                className="w-full flex items-start gap-2 text-start"
                dir="auto"
              >
                <Icon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{line}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {t('onThisDay.yearsAgo', { count: yearsAgo })}
                    {' · '}
                    <span dir="ltr">{item.date}</span>
                  </p>
                </div>
                {clickable && <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100 mt-0.5" />}
              </Row>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function lineFor(item, t) {
  switch (item.kind) {
    case 'weight':
      return t('onThisDay.weight', { weight: item.meta?.weight }) + (item.text ? ` — ${item.text}` : '')
    case 'checkin':
      return item.text
    case 'journey':
      return item.text
    case 'moment':
      return item.text || t('onThisDay.moment')
    case 'book':
      return t('onThisDay.book', { title: item.text })
    default:
      return item.text
  }
}
